import React from "react";

// Base UI
import { Theme, mergeOverrides } from "baseui";
import { Overrides } from "baseui/overrides";
import { Notification as BaseNotification } from "baseui/notification";
import { ToastProps as BaseNotificationProps, ToastOverrides } from "baseui/toast";
import baseuiTheme from "../../../../styling/baseui_theme";

// Carbon Icons
import {
  CheckmarkFilled,
  ErrorFilled,
  InformationFilled,
  WarningAltFilled,
} from "@carbon/icons-react";

import styled from "styled-components";

export enum NotificationColorSchemeEnum {
  LIGHT = "light",
  DARK = "dark",
}

export enum NotificationKindEnum {
  INFO = "info",
  POSITIVE = "positive",
  NEGATIVE = "negative",
  WARNING = "warning",
}

export interface NotificationProps extends BaseNotificationProps {
  colorScheme?: NotificationColorSchemeEnum;
  customIcon?: React.ReactNode;
  showIcon?: boolean;
  kind?: NotificationKindEnum;
  isFullWidth?: boolean;
  styletronOverrides?: Overrides<ToastOverrides>;
  dataTest?: string;
}

export type BulletedNotificationProps = Omit<NotificationProps, "children"> & {
  bulletedMessages: string[];
  notificationHeader: React.ReactNode;
  retainListFormatForOneBullet?: boolean;
  dataTest: string;
};

interface NotificationKindSettings {
  icon: React.ReactNode;
  colorSchemeOverrides: {
    [NotificationColorSchemeEnum.DARK]: Overrides<ToastOverrides>;
    [NotificationColorSchemeEnum.LIGHT]: Overrides<ToastOverrides>;
  };
}

const NOTIFICATION_KINDS: NotificationKindEnum[] = [
  NotificationKindEnum.INFO,
  NotificationKindEnum.NEGATIVE,
  NotificationKindEnum.POSITIVE,
  NotificationKindEnum.WARNING,
];

const NOTIFICATION_COLOR_SCHEMES: NotificationColorSchemeEnum[] = [
  NotificationColorSchemeEnum.DARK,
  NotificationColorSchemeEnum.LIGHT,
];

const DEFAULT_COLOR_SCHEME = NotificationColorSchemeEnum.DARK;

const DEFAULT_KIND = NotificationKindEnum.INFO;

const DEFAULT_NOTIFICATION_OVERRIDES = (theme: Theme) => ({
  CloseIcon: {
    style: {
      alignSelf: "center",
      width: theme.sizing.scale800,
      height: theme.sizing.scale800,
    },
    props: {
      "data-test": "close-icon",
    },
  },
  InnerContainer: {
    style: {
      alignItems: "center",
      display: "flex",
      fontWeight: 500,
      gap: "1em",
    },
  },
});

const NOTIFICATION_SETTING_INFO: NotificationKindSettings = {
  icon: <InformationFilled size={20} />,
  colorSchemeOverrides: {
    [NotificationColorSchemeEnum.DARK]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.mono1000,
          color: baseuiTheme.colors.white,
        },
      },
    },
    [NotificationColorSchemeEnum.LIGHT]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.mono100,
          color: baseuiTheme.colors.mono1000,
        },
      },
    },
  },
};

const NOTIFICATION_SETTING_NEGATIVE: NotificationKindSettings = {
  icon: <ErrorFilled size={16} />,
  colorSchemeOverrides: {
    [NotificationColorSchemeEnum.DARK]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.negative300,
          color: baseuiTheme.colors.white,
        },
      },
    },
    [NotificationColorSchemeEnum.LIGHT]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.negative50,
          color: baseuiTheme.colors.mono1000,
        },
      },
    },
  },
};

const NOTIFICATION_SETTING_POSITIVE: NotificationKindSettings = {
  icon: <CheckmarkFilled size={16} />,
  colorSchemeOverrides: {
    [NotificationColorSchemeEnum.DARK]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.positive,
          color: baseuiTheme.colors.white,
        },
      },
    },
    [NotificationColorSchemeEnum.LIGHT]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.positive50,
          color: baseuiTheme.colors.mono1000,
        },
      },
    },
  },
};

const NOTIFICATION_SETTING_WARNING: NotificationKindSettings = {
  icon: <WarningAltFilled size={16} />,
  colorSchemeOverrides: {
    [NotificationColorSchemeEnum.DARK]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.warning200,
          color: baseuiTheme.colors.mono1000,
        },
      },
    },
    [NotificationColorSchemeEnum.LIGHT]: {
      Body: {
        style: {
          backgroundColor: baseuiTheme.colors.warning50,
          color: baseuiTheme.colors.mono1000,
        },
      },
    },
  },
};

const NOTIFICATION_SETTINGS_MAP = {
  [NotificationKindEnum.INFO]: NOTIFICATION_SETTING_INFO,
  [NotificationKindEnum.NEGATIVE]: NOTIFICATION_SETTING_NEGATIVE,
  [NotificationKindEnum.POSITIVE]: NOTIFICATION_SETTING_POSITIVE,
  [NotificationKindEnum.WARNING]: NOTIFICATION_SETTING_WARNING,
};

const StyledIconContainer = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const MultipleMessagesContainer = styled.div`
  display: "block";
  font-size: ${({ theme }) => theme.typography.ParagraphMedium.fontSize};
  line-height: ${({ theme }) => theme.typography.ParagraphMedium.lineHeight};
`;

const MultipleMessagesHeaderContainer = styled.div`
  display: "block";
  margin-left: ${({ theme }) => theme.sizing.scale600};
`;

const MultipleMessagesListContainer = styled.div`
  ul {
    margin: 0;
  }
`;

export const Notification = ({
  kind = DEFAULT_KIND,
  colorScheme = DEFAULT_COLOR_SCHEME,
  styletronOverrides,
  dataTest = "notification-message",
  ...props
}: NotificationProps) => {
  const sanitizedKind: NotificationKindEnum =
    kind && NOTIFICATION_KINDS.includes(kind) ? kind : NotificationKindEnum.INFO;

  const sanitizedColorScheme =
    colorScheme && NOTIFICATION_COLOR_SCHEMES.includes(colorScheme)
      ? colorScheme
      : NotificationColorSchemeEnum.DARK;

  const notificationKindSettings = NOTIFICATION_SETTINGS_MAP[sanitizedKind];

  const colorSchemeOverrides = notificationKindSettings.colorSchemeOverrides[sanitizedColorScheme];

  const notificationContent = props.showIcon ? (
    <>
      <StyledIconContainer>
        {props.customIcon ? props.customIcon : notificationKindSettings.icon}
      </StyledIconContainer>
      {props.children}
    </>
  ) : (
    <>{props.children}</>
  );

  let componentOverrides = mergeOverrides(
    colorSchemeOverrides,
    DEFAULT_NOTIFICATION_OVERRIDES(baseuiTheme),
  );

  if (props.isFullWidth) {
    componentOverrides = mergeOverrides(componentOverrides, {
      Body: {
        props: {
          "data-test": dataTest,
        },
        style: {
          width: "fill-available",
        },
      },
      InnerContainer: {
        style: {
          width: "fill-available",
        },
      },
    });
  } else {
    componentOverrides = mergeOverrides(componentOverrides, {
      Body: {
        props: {
          "data-test": dataTest,
        },
      },
    });
  }

  if (styletronOverrides) {
    componentOverrides = mergeOverrides(componentOverrides, styletronOverrides);
  }

  return (
    <BaseNotification {...props} overrides={componentOverrides}>
      {notificationContent}
    </BaseNotification>
  );
};

export const BulletedNotification = ({
  kind = DEFAULT_KIND,
  colorScheme = DEFAULT_COLOR_SCHEME,
  bulletedMessages,
  notificationHeader,
  retainListFormatForOneBullet = false,
  styletronOverrides,
  dataTest,
  ...props
}: BulletedNotificationProps) => {
  let messageList = <></>;

  if (bulletedMessages.length > 1 || retainListFormatForOneBullet) {
    messageList = (
      <ul>
        {bulletedMessages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    );
  } else if (bulletedMessages.length === 1) {
    messageList = <>{bulletedMessages[0]}</>;
  }

  return (
    <Notification
      kind={kind}
      colorScheme={colorScheme}
      styletronOverrides={styletronOverrides}
      {...props}
    >
      <MultipleMessagesContainer data-test={dataTest}>
        {(bulletedMessages.length > 1 || retainListFormatForOneBullet) && (
          <MultipleMessagesHeaderContainer>{notificationHeader}</MultipleMessagesHeaderContainer>
        )}
        <MultipleMessagesListContainer>{messageList}</MultipleMessagesListContainer>
      </MultipleMessagesContainer>
    </Notification>
  );
};
